/**
 * Hersteller und Modell je Gerätetyp — der Katalog, der aus
 * `cts_wifi_ir_smi_v32` „CTS · WiFi IR SMI V32" macht.
 *
 * **Das ist kein Raten aus dem Schlüssel, sondern seine Bedeutung.** Der
 * Gerätetyp steht in der Datenbank, weil ihn das Gerät beim Koppeln genannt hat;
 * was er benennt, ist ein bestimmtes Erzeugnis eines bestimmten Herstellers, und
 * das ändert sich nicht. Ein Hersteller, den man aus einer Seriennummer oder
 * einer MAC-Adresse ableitete, wäre etwas anderes — der stünde hier nicht.
 *
 * **Aber nur für bekannte Schlüssel.** `DeviceTypeSchema` ist ein offener String
 * („bekannte Typen: …"), damit ein Fremdgerät über eine neue Integration die API
 * nicht bricht. Für einen Schlüssel, der hier nicht steht, gibt es deshalb
 * **keinen** Hersteller und als Modell den Schlüssel selbst: Was unbekannt ist,
 * wird nicht geraten (Regel 9). Ein Rückfall auf „CTS", weil das das einzige
 * eigene Gerät ist, hinge einem fremden Gerät den falschen Namen an.
 *
 * **Der Typ lässt sich hier nicht erzwingen**, anders als bei
 * `SITE_KIND_LABELS`: Der Vertrag kennt kein Enum, an dem der Compiler eine
 * fehlende Zeile bemerken könnte. Die Liste ist deshalb gegen
 * `DEVICE_TYPE_KEYS` in `@hausmeter/domain` zu halten, wenn dort einer
 * dazukommt — der Katalog vergisst ihn sonst still, und die Seite zeigt den
 * rohen Schlüssel.
 */

/** Was ein Gerätetyp benennt. `manufacturer` ist `null`, wenn es keinen gibt. */
export type DeviceTypeInfo = {
  readonly manufacturer: string | null;
  readonly model: string;
};

/**
 * Die bekannten Typen aus `DEVICE_TYPE_KEYS`.
 *
 * `virtual_manual` und `generic_mqtt` haben **keinen** Hersteller, und das ist
 * kein Rückstand: Das eine ist die Handeingabe, das andere ein beliebiges
 * MQTT-Gerät. Wer dort einen Namen einträgt, erfindet einen.
 */
const DEVICE_TYPES: Record<string, DeviceTypeInfo> = {
  cts_wifi_ir_smi_v32: { manufacturer: "CTS", model: "WiFi IR SMI V32" },
  cts_wifi_ir_wmz_v32: { manufacturer: "CTS", model: "WiFi IR WMZ V32" },
  /**
   * **Hersteller „HausMeter", und hier ist er kein Raten** (HM-109): Das Gateway
   * ist unsere eigene Software, anders als die Geräte, die dahinter stehen.
   */
  hausmeter_gateway: { manufacturer: "HausMeter", model: "Gateway" },
  /**
   * Ein Gerät hinter einem Gateway, dessen Art das Gateway **nicht genannt** hat
   * — der Rückfall. Hat es eine genannt, steht sie als eigener Schlüssel in der
   * Zeile und landet hier gar nicht; dann zeigt die Oberfläche den Schlüssel,
   * und man sieht, welcher Katalogeintrag fehlt.
   */
  gateway_node: { manufacturer: null, model: "Gerät am Gateway" },
  /**
   * Ein **Ladepunkt aus EVCC** hinter dem Gateway (HM-110). Der Schlüssel nennt
   * die Quelle; Hersteller und Modell der Wallbox kommen aus dem Fund und
   * gewinnen in `productLine`, sobald das Gateway sie gemeldet hat. **EVCC ist
   * kein Hersteller** — es ist die Software, über die gelesen wird —, deshalb
   * steht es im Modell und nicht in `manufacturer`.
   */
  evcc_loadpoint: { manufacturer: null, model: "EVCC-Ladepunkt" },
  /** Ein **Speicher aus EVCC** (HM-122) — dieselbe Begründung wie beim Ladepunkt. */
  evcc_battery: { manufacturer: null, model: "EVCC-Speicher" },
  /**
   * Der **Standort einer EVCC-Anlage** (HM-165): das ganze Haus, wie evcc es
   * regelt — Hausakku-Grenzen, Netzladen, Einspeisegrenze, Tarife. Kein Zähler.
   */
  evcc_site: { manufacturer: null, model: "EVCC-Standort" },
  /**
   * **Hinter einem Victron GX** (HM-129). Der Hersteller bleibt offen: Eine
   * Batterie am GX ist oft ein fremdes BMS, ein `pvinverter` ein SMA oder
   * Fronius — was das Gerät über sich sagt, meldet das Gateway.
   */
  victron_battery: { manufacturer: null, model: "Batterie (Victron GX)" },
  victron_solarcharger: { manufacturer: "Victron Energy", model: "MPPT-Laderegler" },
  victron_pvinverter: { manufacturer: null, model: "PV-Wechselrichter (Victron GX)" },
  /**
   * **Wechselrichter hinter dem Gateway** (HM-126, ADR 0039). Der Schlüssel nennt,
   * **wie** gelesen wird, nicht das Erzeugnis: Ob ein SunSpec-Gerät von SMA oder
   * Kostal kommt, meldet das Gateway als Hersteller und gewinnt in `productLine`.
   * Das Modell sagt deshalb die Schnittstelle und nicht „Hybrid" — ob einer ein
   * Hybrid ist, folgt aus seinen Kanälen (`InverterState.kind`).
   */
  sunspec_inverter: { manufacturer: null, model: "Wechselrichter (SunSpec)" },
  fronius_inverter: { manufacturer: "Fronius", model: "Wechselrichter" },
  huawei_inverter: { manufacturer: "Huawei", model: "SUN2000" },
  sungrow_inverter: { manufacturer: "Sungrow", model: "Wechselrichter" },
  goodwe_inverter: { manufacturer: "GoodWe", model: "Wechselrichter" },
  /** Eine DTU liest Mikrowechselrichter — Hoymiles, TSUN; der Hersteller kommt aus dem Fund. */
  opendtu_inverter: { manufacturer: null, model: "Mikrowechselrichter (OpenDTU)" },
  ahoydtu_inverter: { manufacturer: null, model: "Mikrowechselrichter (AhoyDTU)" },
  virtual_manual: { manufacturer: null, model: "Manuelle Eingabe" },
  generic_mqtt: { manufacturer: null, model: "MQTT-Gerät" },
};

/**
 * Der Schlüssel des Gateways (HM-109).
 *
 * **Ein Vergleich mit dieser Konstanten ist kein Raten**, anders als der Blick in einen
 * Schlüssel, um daraus eine Größe abzuleiten (HM-095): Der Gerätetyp **ist** die Kennung
 * des Erzeugnisses, und `hausmeter_gateway` benennt genau eines. Die Web-App darf
 * `@hausmeter/domain` nur für Typen benutzen; die Zeichenkette steht deshalb hier und
 * nicht als Import.
 */
export const GATEWAY_DEVICE_TYPE = "hausmeter_gateway";

export function isGatewayDevice(deviceType: string): boolean {
  return deviceType === GATEWAY_DEVICE_TYPE;
}

/**
 * Der Schlüssel eines EVCC-Ladepunkts (HM-110) — dieselbe Begründung wie beim
 * Gateway. Für ihn gibt es die Wallbox-Ansicht; eine Wallbox aus einer anderen
 * Quelle bekäme einen eigenen Schlüssel, denn Lademodi und Datenpunkte sind die
 * von EVCC.
 */
export const EVCC_LOADPOINT_DEVICE_TYPE = "evcc_loadpoint";

export function isEvccLoadpointDevice(deviceType: string): boolean {
  return deviceType === EVCC_LOADPOINT_DEVICE_TYPE;
}

/** Der Schlüssel eines EVCC-Standorts (HM-165) — dieselbe Begründung wie beim Ladepunkt. */
export const EVCC_SITE_DEVICE_TYPE = "evcc_site";

export function isEvccSiteDevice(deviceType: string): boolean {
  return deviceType === EVCC_SITE_DEVICE_TYPE;
}

/**
 * Die Schlüssel der Speicher (HM-122). Für sie gibt es die Speicheransicht;
 * die Liste ist gegen `STORAGE_DEVICE_TYPES` in `@hausmeter/domain` zu halten.
 */
export const STORAGE_DEVICE_TYPES: readonly string[] = ["evcc_battery", "victron_battery"];

export function isStorageDevice(deviceType: string): boolean {
  return STORAGE_DEVICE_TYPES.includes(deviceType);
}

/**
 * Die Schlüssel der Wechselrichter (HM-126). Für sie gibt es die
 * Wechselrichteransicht; ein Test hält die Liste gegen `INVERTER_DEVICE_TYPES`
 * in `@hausmeter/domain`.
 */
export const INVERTER_DEVICE_TYPES: readonly string[] = [
  "sunspec_inverter",
  "fronius_inverter",
  "huawei_inverter",
  "sungrow_inverter",
  "goodwe_inverter",
  "opendtu_inverter",
  "ahoydtu_inverter",
  "victron_solarcharger",
  "victron_pvinverter",
];

export function isInverterDevice(deviceType: string): boolean {
  return INVERTER_DEVICE_TYPES.includes(deviceType);
}

export function deviceTypeInfo(deviceType: string): DeviceTypeInfo {
  return DEVICE_TYPES[deviceType] ?? { manufacturer: null, model: deviceType };
}

/**
 * Hersteller und Modell als eine Zeile — „CTS WiFi IR SMI V32".
 *
 * Ohne bekannten Hersteller bleibt das Modell allein stehen. Ein vorangestelltes
 * „Unbekannt" wäre die Sorte Platzhalter, die diese Seite nicht mehr führt.
 */
export function deviceTypeLabel(deviceType: string): string {
  const { manufacturer, model } = deviceTypeInfo(deviceType);
  return manufacturer === null ? model : `${manufacturer} ${model}`;
}

/**
 * Was für ein Gerät das ist — **zuerst das, was es selbst gemeldet hat.**
 *
 * `product` und `manufacturer` stehen seit HM-041 in `hello`; sie kommen aus
 * demselben Board-Profil, aus dem die Firmware ihren `deviceType` bildet. Damit
 * benennt sich ein neues Erzeugnis selbst, statt darauf zu warten, dass jemand
 * den Katalog oben nachzieht — und genau dafür sendet die Firmware sie.
 *
 * **Der Katalog bleibt der Rückfall**, denn ein Gerät, das noch nie verbunden
 * war, hat nichts gemeldet: In der Geräteliste stünde sonst zwischen zwei
 * benannten Zeilen eine leere. Gemischt wird dabei nicht — entweder gilt, was
 * das Gerät gesagt hat, oder der Katalog. Ein Hersteller aus der einen und ein
 * Modell aus der anderen Quelle ergäbe eine Bezeichnung, die es nirgends gibt.
 */
export function productLine(device: {
  readonly deviceType: string;
  readonly product: string | null;
  readonly manufacturer: string | null;
}): string {
  const reported = [device.manufacturer, device.product].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );

  return reported.length === 0 ? deviceTypeLabel(device.deviceType) : reported.join(" · ");
}

/**
 * Nur das Erzeugnis, für eine schmale Spalte (UI-Durchsicht U8). In der
 * Geräteliste stand „Christians Technikshop · CTS WiFi …“ und wurde abgeschnitten,
 * bevor das Modell kam; der Hersteller steht beim Überfahren (`productLine`).
 * Ohne gemeldetes Erzeugnis gilt der Katalog, wie bei `productLine`.
 */
export function productName(device: {
  readonly deviceType: string;
  readonly product: string | null;
  readonly manufacturer: string | null;
}): string {
  const product = device.product?.trim();
  return product !== undefined && product.length > 0 ? product : productLine(device);
}

